"use client";

import { useEffect, useRef, useState, type ElementType } from "react";

type RevealProps = {
  as?: ElementType;
  className?: string;
  /** Also toggle `stag-on` so `.stag-child` children animate in sequence. */
  stagger?: boolean;
  children: React.ReactNode;
};

/** Fades content in when scrolled into view (adds `.in` to the `.reveal` base class). */
export default function Reveal({
  as: Tag = "div",
  className = "",
  stagger = false,
  children,
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.14 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const state = visible ? ` in${stagger ? " stag-on" : ""}` : "";
  return (
    <Tag ref={ref} className={`reveal ${className}${state}`.trim()}>
      {children}
    </Tag>
  );
}
